import camelcaseKeys from 'camelcase-keys'

type SpecTransform = (spec: Record<string, unknown>) => Record<string, unknown>

interface ArtifactKindConfig {
  // Converts the wire-format snake_case spec into the camelCase shape the
  // artifact component expects.
  transform: SpecTransform
  // Tool names whose tool_use block renders inline as this artifact instead
  // of a tool chip.
  toolNames: readonly string[]
}

function chartSpec(spec: Record<string, unknown>): Record<string, unknown> {
  return camelcaseKeys(spec, { deep: true })
}

function downloadSpec(spec: Record<string, unknown>): Record<string, unknown> {
  const top = camelcaseKeys(spec) as Record<string, unknown>
  if (Array.isArray(top.files)) {
    top.files = (top.files as Record<string, unknown>[]).map((file) =>
      camelcaseKeys(file),
    )
  }
  return top
}

function tableSpec(spec: Record<string, unknown>): Record<string, unknown> {
  // Top-level keys stay snake_case: TableSpec reads `total_row` as sent.
  const top = { ...spec }
  if (Array.isArray(top.columns)) {
    top.columns = (top.columns as Record<string, unknown>[]).map((column) =>
      camelcaseKeys(column),
    )
  }
  return top
}

function transactionsSpec(
  spec: Record<string, unknown>,
): Record<string, unknown> {
  // Just title + transactionIds — both flat scalars. transactionIds
  // comes off the wire as `transaction_ids` so camelcaseKeys at the top
  // level is enough.
  return camelcaseKeys(spec) as Record<string, unknown>
}

function checksSpec(spec: Record<string, unknown>): Record<string, unknown> {
  // Just title + checkIds — both flat scalars. checkIds comes off the
  // wire as `check_ids` so camelcaseKeys at the top level is enough.
  return camelcaseKeys(spec) as Record<string, unknown>
}

function bulkEditPreviewSpec(
  spec: Record<string, unknown>,
): Record<string, unknown> {
  // deep:true so that fieldUpdates' keys ("category_detail" → "categoryDetail")
  // line up with the camelCased sample-row keys; the artifact then renders
  // labels and looks up "current" values using the same key on both sides.
  return camelcaseKeys(spec, { deep: true }) as Record<string, unknown>
}

function removePreviewSpec(
  spec: Record<string, unknown>,
): Record<string, unknown> {
  // deep:true so each sample row's snake_case keys (account_number,
  // statement_file_name) become camelCase to match the row type.
  return camelcaseKeys(spec, { deep: true }) as Record<string, unknown>
}

function mapSpec(spec: Record<string, unknown>): Record<string, unknown> {
  // deep:true converts each point's `transaction_ids` → `transactionIds`.
  return camelcaseKeys(spec, { deep: true }) as Record<string, unknown>
}

function reportEditPreviewSpec(
  spec: Record<string, unknown>,
): Record<string, unknown> {
  // Shallow only: old_section/new_section keep their internal snake_case
  // keys untouched, matching ReportSection's wire-verbatim convention
  // (analysis/stores/types.ts) so they can be rendered by the same report
  // section components (ReportTable/ReportChart) without a second mapping.
  return camelcaseKeys(spec) as Record<string, unknown>
}

// Single source of truth for the artifact kinds the chat can render.
// Adding a kind here is the whole client-side wiring apart from its
// component: the `ArtifactKind` union, the spec transform, and the
// inline-render tool predicate all derive from this record, and the
// component map in ArtifactView is a Record<ArtifactKind, …> so the
// compiler flags a missing renderer.
export const ARTIFACT_KIND_CONFIG = {
  chart: { transform: chartSpec, toolNames: ['render_chart'] },
  table: { transform: tableSpec, toolNames: ['render_table'] },
  transactions: {
    transform: transactionsSpec,
    toolNames: ['render_transactions'],
  },
  checks: { transform: checksSpec, toolNames: ['render_checks'] },
  map: { transform: mapSpec, toolNames: ['render_map'] },
  download: {
    transform: downloadSpec,
    toolNames: ['export_transactions', 'export_checks'],
  },
  bulk_edit_preview: {
    transform: bulkEditPreviewSpec,
    toolNames: ['preview_bulk_edit'],
  },
  remove_preview: {
    transform: removePreviewSpec,
    toolNames: ['preview_remove_transactions'],
  },
  report_edit_preview: {
    transform: reportEditPreviewSpec,
    toolNames: ['propose_report_edit'],
  },
  case_fact_preview: {
    transform: (spec) => camelcaseKeys(spec),
    toolNames: ['propose_case_fact'],
  },
  choices: { transform: (spec) => spec, toolNames: ['render_choices'] },
} as const satisfies Record<string, ArtifactKindConfig>

export type ArtifactKind = keyof typeof ARTIFACT_KIND_CONFIG

export function isArtifactKind(kind: string): kind is ArtifactKind {
  return kind in ARTIFACT_KIND_CONFIG
}

export function camelcaseSpec(
  kind: ArtifactKind,
  spec: Record<string, unknown>,
): Record<string, unknown> {
  return ARTIFACT_KIND_CONFIG[kind].transform(spec)
}

// Tool names whose successful result renders inline as an artifact.
export const RENDER_TOOL_NAMES: ReadonlySet<string> = new Set(
  Object.values(ARTIFACT_KIND_CONFIG).flatMap((config) => config.toolNames),
)
